// src/Pages/Admin/UserManagement.jsx
import React, { useEffect, useState, useMemo } from "react";
import Layout from "../../Components/Layout/Layout";
import {
  Box, Typography, Table, TableHead, TableRow, TableCell, TableBody,
  CircularProgress, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, FormControl, InputLabel, Select,
  MenuItem, Autocomplete, Button, Chip, Stack,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router-dom";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

export default function UserManagement() {
  /* ───── state */
  const [users, setUsers] = useState([]);
  const [locOptions, setLocOptions] = useState([]);
  const [teams, setTeams] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);

  // Фильтры
  const [filter, setFilter] = useState({
    firstName: "",
    lastName: "",
    department: "",
    team: "",
    role: "",
  });

  // Модалка для редактирования
  const [openEdit, setOpenEdit] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState({
    email: "", role: "", first_name: "", last_name: "",
    password: "", allowed_locations: [], active: true, teams: [],
  });

  // Модалка для создания
  const [openCreate, setOpenCreate] = useState(false);
  const [createForm, setCreateForm] = useState({
    chat_id: "", first_name: "", last_name: "", email: "", role: "User", team: null,
  });

  const navigate = useNavigate();
  const authToken = localStorage.getItem("auth_token") || "";

  /* ───── fetch init */
  useEffect(() => {
    (async () => {
      await Promise.all([
        fetchUsers(), fetchLocations(), fetchTeams(), fetchDepartments()
      ]);
    })();
    // eslint-disable-next-line
  }, []);

  async function fetchUsers() {
    setLoading(true);
    const r = await fetch(`${API_URL}/admin/users?auth_token=${encodeURIComponent(authToken)}`);
    const j = await r.json();
    if (j.status === "success") setUsers(j.users || []);
    setLoading(false);
  }
  async function fetchLocations() {
    const r = await fetch(`${API_URL}/api/admin/allowed-locations`);
    const j = await r.json();
    const pts = (j.points || []).map(p => ({ id: `P${p.id}`, label: `📍 ${p.name || `Zone #${p.id}`}` }));
    const polys = (j.polygons || []).map(a => ({ id: `A${a.id}`, label: `🔷 ${a.name || `Area #${a.id}`}` }));
    setLocOptions([...pts, ...polys]);
  }
  async function fetchTeams() {
    const r = await fetch(`${API_URL}/admin/teams?auth_token=${encodeURIComponent(authToken)}`);
    const j = await r.json(); if (j.status === "success") setTeams(j.teams || []);
  }
  async function fetchDepartments() {
    const r = await fetch(`${API_URL}/admin/departments?auth_token=${encodeURIComponent(authToken)}`);
    const j = await r.json(); if (j.status === "success") setDepartments(j.departments || []);
  }

  /* ---- фильтрация пользователей */
  const filteredUsers = useMemo(() => {
    return users.filter(u => {
      const firstNameMatch = u.first_name.toLowerCase().includes(filter.firstName.toLowerCase());
      const lastNameMatch = u.last_name.toLowerCase().includes(filter.lastName.toLowerCase());
      const roleMatch = filter.role ? u.role === filter.role : true;
      const departmentMatch = filter.department
        ? (u.assignments || []).some(a => a.department_id === filter.department)
        : true;
      const teamMatch = filter.team
        ? (u.assignments || []).some(a => a.team_id === filter.team)
        : true;

      return firstNameMatch && lastNameMatch && roleMatch && departmentMatch && teamMatch;
    });
  }, [users, filter]);

  /* ───── helpers */
  const depName = id => departments.find(d => d.id === id)?.name || "—";

  const assignmentsOf = u =>
    (u.assignments || []).map(a => `${a.department_name} / ${a.team_name}`);

  /* ───── modal open/close */
  const openModal = u => {
    setEditUser(u);
    const userTeams = u.assignments
      ?.map(a => teams.find(t => t.id === a.team_id))
      .filter(Boolean) || [];
    const locs = (u.allowed_locations || "").split(",")
      .map(id => locOptions.find(o => o.id === id)).filter(Boolean);
    setForm({
      email: u.email, role: u.role, first_name: u.first_name, last_name: u.last_name,
      password: "", allowed_locations: locs, active: u.active, teams: userTeams,
    });
    setOpenEdit(true);
  };

  const closeModal = () => { setOpenEdit(false); setEditUser(null); };

  const openCreateModal = () => {
    setCreateForm({ chat_id: "", first_name: "", last_name: "", email: "", role: "User", team: null });
    setOpenCreate(true);
  };

  const closeCreateModal = () => { setOpenCreate(false); };

  /* ───── save */
  const saveUser = async () => {
    if (!editUser) return;
    const body = {
      auth_token: authToken,
      role: form.role,
      first_name: form.first_name,
      last_name: form.last_name,
      ...(form.password ? { password: form.password } : {}),
      allowed_locations: form.allowed_locations.map(o => o.id).join(","),
      active: form.active,
      team_ids: form.teams.map(t => t.id),
    };
    const r = await fetch(`${API_URL}/admin/users/${editUser.chat_id}`, {
      method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body)
    });
    const j = await r.json();
    if (j.status === "success") { fetchUsers(); closeModal(); }
    else alert(j.message);
  };

  /* ───── create user */
  const createUser = async () => {
    if (!createForm.chat_id || !createForm.first_name || !createForm.last_name || 
        !createForm.email || !createForm.role || !createForm.team) {
      alert("All fields are required");
      return;
    }
    const body = {
      auth_token: authToken,
      chat_id: createForm.chat_id,
      first_name: createForm.first_name,
      last_name: createForm.last_name,
      email: createForm.email,
      role: createForm.role,
      team_id: createForm.team.id,
    };
    const r = await fetch(`${API_URL}/admin/users`, {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body)
    });
    const j = await r.json();
    if (j.status === "success") { fetchUsers(); closeCreateModal(); }
    else alert(j.message);
  };

  /* ───── delete, toggleActive */
  const delUser = async id => {
    if (!window.confirm("Delete user?")) return;
    const r = await fetch(`${API_URL}/admin/users/${id}?auth_token=${encodeURIComponent(authToken)}`, { method: "DELETE" });
    const j = await r.json(); if (j.status === "success") fetchUsers(); else alert(j.message);
  };
  const toggleActive = async (id, val) => {
    const r = await fetch(`${API_URL}/admin/users/${id}`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ auth_token: authToken, active: val })
    });
    const j = await r.json(); if (j.status === "success") fetchUsers(); else alert(j.message);
  };

  /* ───── render */
  return (
    <Layout>
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h5">User Management</Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateModal}>
            Create User
          </Button>
        </Box>

        {/* Фильтры */}
        <Box sx={{ display: "flex", gap: 2, mt: 2, mb: 2, flexWrap: "wrap" }}>
          <TextField
            label="First Name"
            value={filter.firstName}
            onChange={e => setFilter({ ...filter, firstName: e.target.value })}
            sx={{ minWidth: 150 }}
          />
          <TextField
            label="Last Name"
            value={filter.lastName}
            onChange={e => setFilter({ ...filter, lastName: e.target.value })}
            sx={{ minWidth: 150 }}
          />
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Role</InputLabel>
            <Select
              value={filter.role}
              onChange={e => setFilter({ ...filter, role: e.target.value })}
              label="Role"
            >
              <MenuItem value="">All</MenuItem>
              {["User", "Supervisor", "Manager", "HOD", "HR", "CEO", "admin"].map(r => (
                <MenuItem key={r} value={r}>{r}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Department</InputLabel>
            <Select
              value={filter.department}
              onChange={e => setFilter({ ...filter, department: e.target.value })}
              label="Department"
            >
              <MenuItem value="">All</MenuItem>
              {departments.map(d => (
                <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Team</InputLabel>
            <Select
              value={filter.team}
              onChange={e => setFilter({ ...filter, team: e.target.value })}
              label="Team"
            >
              <MenuItem value="">All</MenuItem>
              {teams.map(t => (
                <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {loading ? <CircularProgress sx={{ mt: 2 }} /> :
          <Table sx={{ mt: 2 }}>
            <TableHead>
              <TableRow>
                <TableCell>Email</TableCell><TableCell>Role</TableCell>
                <TableCell>Name</TableCell><TableCell>Assignments</TableCell>
                <TableCell>Active</TableCell><TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers.map(u => {
                const asg = assignmentsOf(u);
                return (
                  <TableRow key={u.chat_id}>
                    <TableCell>{u.email}</TableCell>
                    <TableCell>{u.role}</TableCell>
                    <TableCell>{u.first_name} {u.last_name}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        {asg.length ? asg.map((a, i) =>
                          <Chip key={i} label={a} sx={{ borderRadius: 2, bgcolor: "#f0f0f0" }} />
                        ) : "—"}
                      </Stack>
                    </TableCell>
                    <TableCell>{u.active ? "✅" : "❌"}</TableCell>
                    <TableCell>
                      <IconButton onClick={() => navigate(`/admin/timesheet_detail/${u.chat_id}`)}>
                        <VisibilityIcon />
                      </IconButton>
                      <IconButton color="primary" onClick={() => openModal(u)}><EditIcon /></IconButton>
                      <IconButton color={u.active ? "warning" : "success"}
                        onClick={() => toggleActive(u.chat_id, !u.active)}>
                        {u.active ? <BlockIcon /> : <CheckCircleIcon />}
                      </IconButton>
                      <IconButton color="error" onClick={() => delUser(u.chat_id)}><DeleteIcon /></IconButton>
                    </TableCell>
                  </TableRow>);
              })}
            </TableBody>
          </Table>}
      </Box>

      {/* ───── edit modal */}
      <Dialog open={openEdit} onClose={closeModal} fullWidth maxWidth="sm">
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
            <TextField label="Email" value={form.email} disabled fullWidth />
            <FormControl fullWidth>
              <InputLabel id="role">Role</InputLabel>
              <Select labelId="role" name="role" value={form.role}
                onChange={e => setForm({ ...form, role: e.target.value })} label="Role">
                {["User", "Supervisor", "Manager", "HOD", "HR", "CEO", "admin"].map(r =>
                  <MenuItem key={r} value={r}>{r}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField label="First Name" value={form.first_name}
              onChange={e => setForm({ ...form, first_name: e.target.value })} fullWidth />
            <TextField label="Last Name" value={form.last_name}
              onChange={e => setForm({ ...form, last_name: e.target.value })} fullWidth />
            <TextField label="New Password" type="password" value={form.password}
              onChange={e => setForm({ ...form, password: e.target.value })}
              helperText="Leave blank to keep current" fullWidth />
            <Autocomplete
              multiple
              options={teams}
              getOptionLabel={t => `${depName(t.department_id)} / ${t.name}`}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              value={form.teams}
              onChange={(e, v) => setForm({ ...form, teams: v })}
              renderInput={p => <TextField {...p} label="Teams" placeholder="Select teams" />}
              fullWidth
            />
            <Autocomplete
              multiple options={locOptions} getOptionLabel={o => o.label}
              value={form.allowed_locations}
              onChange={(e, v) => setForm({ ...form, allowed_locations: v })}
              renderInput={p => <TextField {...p} label="Allowed Locations" placeholder="Zones & areas" />}
            />
            <FormControl fullWidth>
              <InputLabel id="active">Active</InputLabel>
              <Select labelId="active" value={String(form.active)}
                onChange={e => setForm({ ...form, active: e.target.value === "true" })} label="Active">
                <MenuItem value="true">Active</MenuItem>
                <MenuItem value="false">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeModal}>Cancel</Button>
          <Button variant="contained" onClick={saveUser}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* ───── create modal */}
      <Dialog open={openCreate} onClose={closeCreateModal} fullWidth maxWidth="sm">
        <DialogTitle>Create User</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
            <TextField
              label="Telegram Chat ID"
              value={createForm.chat_id}
              onChange={e => setCreateForm({ ...createForm, chat_id: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="First Name"
              value={createForm.first_name}
              onChange={e => setCreateForm({ ...createForm, first_name: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Last Name"
              value={createForm.last_name}
              onChange={e => setCreateForm({ ...createForm, last_name: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Email"
              value={createForm.email}
              onChange={e => setCreateForm({ ...createForm, email: e.target.value })}
              fullWidth
              required
            />
            <FormControl fullWidth required>
              <InputLabel id="create-role">Role</InputLabel>
              <Select
                labelId="create-role"
                value={createForm.role}
                onChange={e => setCreateForm({ ...createForm, role: e.target.value })}
                label="Role"
              >
                {["User", "Supervisor", "Manager", "HOD", "HR", "CEO", "admin"].map(r =>
                  <MenuItem key={r} value={r}>{r}</MenuItem>)}
              </Select>
            </FormControl>
            <Autocomplete
              options={teams}
              getOptionLabel={t => `${depName(t.department_id)} / ${t.name}`}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              value={createForm.team}
              onChange={(e, v) => setCreateForm({ ...createForm, team: v })}
              renderInput={p => <TextField {...p} label="Team" placeholder="Select team" required />}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCreateModal}>Cancel</Button>
          <Button variant="contained" onClick={createUser}>Create</Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
}
